import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { X } from "@phosphor-icons/react";

/**
 * Lightweight, dependency-free step-by-step product tour.
 * Spotlights a target element (via CSS selector) and shows a tooltip card
 * next to it with title/description and next/back/skip controls.
 *
 * steps: [{ target: "[data-tour='x']", title, content }]
 */
export default function GuidedTour({ steps, active, onFinish }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);

  const measure = useCallback(() => {
    const step = steps[stepIndex];
    const el = step && document.querySelector(step.target);
    setRect(el ? el.getBoundingClientRect() : null);
  }, [steps, stepIndex]);

  useEffect(() => {
    if (!active) return;
    setStepIndex(0);
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const step = steps[stepIndex];
    const el = step && document.querySelector(step.target);
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
    const t = setTimeout(measure, 250);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [active, stepIndex, steps, measure]);

  if (!active || steps.length === 0) return null;

  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;
  const pad = 8;

  const spotlightStyle = rect
    ? {
        top: rect.top - pad,
        left: rect.left - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
        boxShadow: "0 0 0 9999px rgba(15,23,42,0.72)",
      }
    : { top: "50%", left: "50%", width: 0, height: 0, boxShadow: "0 0 0 9999px rgba(15,23,42,0.72)" };

  const cardWidth = 320;
  const tooltipTop = rect
    ? Math.min(Math.max(rect.bottom + pad + 12, 16), window.innerHeight - 200)
    : window.innerHeight / 2 - 90;
  const tooltipLeft = rect
    ? Math.min(Math.max(rect.left, 16), window.innerWidth - cardWidth - 16)
    : window.innerWidth / 2 - cardWidth / 2;

  return (
    <>
      <div
        className="fixed z-[9998] rounded-lg pointer-events-none transition-all duration-200 ease-out"
        style={spotlightStyle}
      />
      <div
        className="fixed z-[9999] rounded-md border border-slate-200 bg-white p-5 shadow-xl transition-all duration-200 ease-out"
        style={{ top: tooltipTop, left: tooltipLeft, width: cardWidth }}
        data-testid="guided-tour-card"
      >
        <div className="mb-2 flex items-start justify-between">
          <span className="label-uppercase text-slate-400">
            Paso {stepIndex + 1} de {steps.length}
          </span>
          <button
            onClick={onFinish}
            className="text-slate-400 hover:text-slate-700"
            data-testid="guided-tour-close"
            aria-label="Cerrar tutorial"
          >
            <X size={16} />
          </button>
        </div>
        <h4 className="font-display font-bold text-ink mb-1.5">{step.title}</h4>
        <p className="text-sm text-slate-600 mb-4">{step.content}</p>
        <div className="flex items-center justify-between">
          <button
            onClick={onFinish}
            className="text-xs font-medium text-slate-500 hover:text-ink"
            data-testid="guided-tour-skip"
          >
            Saltar tutorial
          </button>
          <div className="flex gap-2">
            {stepIndex > 0 && (
              <Button variant="outline" size="sm" onClick={() => setStepIndex((i) => i - 1)}>
                Atrás
              </Button>
            )}
            <Button
              size="sm"
              data-testid="guided-tour-next"
              onClick={() => (isLast ? onFinish() : setStepIndex((i) => i + 1))}
            >
              {isLast ? "Terminar" : "Siguiente"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
